import axiosInstance from "@/lib/axiosInstance";
import { buildFilterByParam } from "@/utils/buildFilterByParam";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import { clientsEndpoints } from "../clients.endpoints";
import type { BrandDetailsTabDataResponse } from "./brands.queries.types";
import type { FilterValue } from "@/store/tableFilterStore";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";

export const getBrandsData = async ({
  clientId,
  search,
  sortBy,
  sortOrder,
  page,
  pageSize,
  filters,
  signal,
}: {
  clientId: string;
  search: string;
  sortBy: string;
  sortOrder: string;
  page: number;
  pageSize: number;
  filters?: Array<{ filterBy: string; filterValue: FilterValue }>;
  signal: AbortSignal;
}): Promise<BrandDetailsTabDataResponse> => {
  const params = new URLSearchParams();

  const filterBy = buildFilterByParam(filters, {
    getValue: (f) => f.filterValue.label ?? f.filterValue.value,
  });
  if (filterBy) {
    params.append("filterBy", filterBy);
    params.append("page", page.toString());
  }

  if (search) {
    params.append("search", search);
  }

  if (sortBy) {
    params.append("sortBy", sortBy);
  }

  if (sortOrder) {
    params.append("sortOrder", sortOrder);
  }

  params.append("limit", pageSize.toString());

  const response = await axiosInstance.get(
    `${clientsEndpoints.getBrands}/${clientId}?${getQueryParamsWithCustomQueryObj(params)}`,
    { signal },
  );
  return response.data;
};

// Client-specific dropdown for brand name filter: /client/brands/{clientId}/dropdown
// API returns: { id, name, clientBrandId }
// We transform it to: { id, label, value } for filter compatibility
// export const getBrandDropdown = async (
//   clientId: string,
//   search?: string,
//   signal?: AbortSignal,
//   key?: string,
// ): Promise<{
//   success: boolean;
//   data: Array<{ id: string; label: string; value: string }>;
//   pagination?: {
//     limit: number;
//     nextCursor: number | null;
//     hasMore: boolean;
//   };
// }> => {
//   const params = new URLSearchParams();

//   if (search) {
//     params.append("search", search);
//   }

//   const queryString = getQueryParamsWithCustomQueryObj(params);
//   console.log("THIS IS ", queryString);

//   const response = await axiosInstance.get<{
//     success: boolean;
//     data: Array<{ id: string; name: string; clientBrandId: string }>;
//     pagination?: {
//       limit: number;
//       nextCursor: number | null;
//       hasMore: boolean;
//     };
//   }>(`${clientsEndpoints.getBrands}/${clientId}/dropdown?${queryString}`, { signal });

//   // Transform API response to filter-compatible format
//   // Map: { id, name, clientBrandId } -> { id, label, value }
//   // Use id as value for backend filtering, name as label for display
//   return {
//     success: response.data.success,
//     data: response.data.data.map((item) => ({
//       id: item.id,
//       label: item.name, // Display name in the filter chip
//       value: item.id, // Use id for backend filtering (backend typically expects ID)
//     })),
//     pagination: response.data.pagination,
//   };
// };

export const getBrandDropdown = async (
  clientId: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
  searchParams?: TSearchParams,
) => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  if (key == "locationsCount") {
    params.append("type", "ClientBrands");
    params.append("field", "locationsCount");
    // params.append("field", "value");
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
    params.append("modify", encodeURI(`{"source":["locationsCount"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("filterBy", "clientId");
    if (searchParams?.clientId) params.append("filterValue", searchParams?.clientId);
  }

  if (key === "activeWorkOrdersCount") {
    params.append("type", "ClientBrands");
    params.append("field", "activeWorkOrdersCount");
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
    params.append("modify", encodeURI(`{"source":["activeWorkOrdersCount"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("filterBy", "clientId");
    if (searchParams?.clientId) params.append("filterValue", searchParams?.clientId);
  }

  if (key === "contactsCount") {
    params.append("type", "ClientBrands");
    params.append("field", "contactsCount");
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
    params.append("modify", encodeURI(`{"source":["contactsCount"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("filterBy", "clientId");
    if (searchParams?.clientId) params.append("filterValue", searchParams?.clientId);
  }

  if (key == "brandName") {
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  // Choose base URL once based on key and make a single HTTP call
  const baseUrl =
    key === "brandName" ? `${clientsEndpoints.getBrands}/${clientId}/dropdown` : "dropdown";

  const { data } = await axiosInstance.get(`${baseUrl}?${queryString}`, {
    signal,
  });

  // All variants return {success, data, ...}; ApiFilterChip reads .data
  return data;
};
